import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import Web3 from "web3";
import { AbiItem } from "web3-utils";
import WalletConnectProvider from "@walletconnect/web3-provider";
import EthContract from "web3-eth-contract";
import {
  marketplaceContractAddress,
  marketplaceContractAbi,
} from "contract/marketplace";
import {
  nftMintingContractAddress,
  nftMintingContractAbi,
} from "contract/nftMinting";

import {
  StateType,
  Web3ConnectPayloadType,
  NetworkProps,
} from "interfaces/web3Props";
import { ethers } from "ethers";
import { NftSwapV4 } from "@traderxyz/nft-swap-sdk";
import { paymentTokenSelect, paymentList } from "components/data/selectData";
import { ContractUtility } from "utility";
import openNotification from "components/common/toastMessage/toastMessage";

export const initialState: StateType = {
  web3: null,
  marketPlaceContract: null,
  nftMintingContract: null,
  marketplaceContractAddress: "0x418d541b05D932371EC148852448F7445b2b73dF",
  nftMintingContractAddress: "0x0DA516A3DC76bef71f6b0459D6D01eD64EEf26e4",
  account: null,
  web3LoadingErrorMessage: "",
  web3Loading: false,
  ErrorMessage: "",
  provider: null,
  etherProvider: null,
  signer: null,
  swapSdk: null,
  chainId: null,
  paymentTokens: null,
  networkName: null,
  moralisConfig: null,
};
declare global {
  interface Window {
    ethereum: any; // 👈️ turn off type checking
  }
}
export const loadBlockchain = createAsyncThunk(
  "loadwalletcoonnect",
  async (_, thunkAPI) => {
    try {
      //if (Web3.givenProvider && CommonUtility.networkShould(Web3.givenProvider.chainId)) {
      if (Web3.givenProvider) {
        await Web3.givenProvider.enable();
        const web3 = new Web3(Web3.givenProvider);
        let networkName = await web3.eth.net.getNetworkType();
        let account: any = await web3.eth.getAccounts();
        account = account[0];
        let contractAddress = ContractUtility.getContractAddress(networkName);
        const marketPlaceContract: EthContract.Contract = new web3.eth.Contract(
          marketplaceContractAbi as AbiItem[],
          contractAddress.marketplaceContractAddress
        );

        const nftMintingContract: EthContract.Contract = new web3.eth.Contract(
          nftMintingContractAbi as AbiItem[],
          contractAddress.mintingContractAddress
        );
        const chainId: any = (await web3?.eth.getChainId()).toString();
        if (
          chainId === "1" ||
          chainId === "3" ||
          chainId === "4" ||
          chainId === "137"
        ) {
        } else {
          openNotification(
            "Invalid Network",
            "Please switch to ethereum or polygon network",
            "warning"
          );
        }
        const etherProvider = new ethers.providers.Web3Provider(
          window.ethereum
        );
        const signer = etherProvider.getSigner();
        const swapSdk = new NftSwapV4(etherProvider, signer, 137);

        const paymentTokens = paymentList(chainId);

        let moralisConfig = ContractUtility.getMoralisKeys(networkName);

        return {
          web3,
          account,
          marketPlaceContract,
          nftMintingContract,
          marketplaceContractAddress:
            contractAddress.marketplaceContractAddress,
          nftMintingContractAddress: contractAddress.mintingContractAddress,
          etherProvider,
          signer,
          swapSdk,
          chainId,
          paymentTokens,
          networkName,
          moralisConfig,
        };
      } else {
        console.log("error connecting to metamask");
        return {
          web3LoadingErrorMessage: "error connecting to metamask",
        };
      }
    } catch (err) {
      console.log(err);
    }
  }
);

// Connect with Wallet of users choice
export const loadWalletConnect = createAsyncThunk(
  "LoadWalletConnect",
  async (_, thunkAPI) => {
    try {
      const provider = new WalletConnectProvider({
        rpc: {
          56: "https://bsc-dataseed.binance.org/",
        },
        chainId: 56,
      });

      if (provider) {
        await provider.enable();
        const web3 = new Web3(provider as any);

        let account: any = await web3.eth.getAccounts();
        account = account[0];

        const marketPlaceContract: EthContract.Contract = new web3.eth.Contract(
          marketplaceContractAbi as AbiItem[],
          ContractUtility.config.ropsten.marketplaceContractAddress
        );

        const nftMintingContract: EthContract.Contract = new web3.eth.Contract(
          nftMintingContractAbi as AbiItem[],
          ContractUtility.config.ropsten.mintingContractAddress
        );

        return {
          web3,
          account,
          marketPlaceContract,
          nftMintingContract,
          marketplaceContractAddress:
            ContractUtility.config.ropsten.marketplaceContractAddress,
          nftMintingContractAddress:
            ContractUtility.config.ropsten.mintingContractAddress,
        };
      } else {
        return {
          web3LoadingErrorMessage:
            "Please install an Ethereum-compatible browser or extension like MetaMask to use this dApp!",
        };
      }
    } catch (err) {
      console.log(err);
    }
  }
);

// Connect with Wallet of users choice
export const logoutBlockchain = createAsyncThunk(
  "LoadWalletConnect",
  async (_, thunkAPI) => {
    try {
      const provider = new WalletConnectProvider({
        rpc: {
          56: "https://bsc-dataseed.binance.org/",
        },
        chainId: 56,
      });

      if (provider) {
        await provider.enable();
        const web3 = null;

        let account: any = null;

        const marketPlaceContract: EthContract.Contract = null;

        const nftMintingContract: EthContract.Contract = null;

        return {
          web3,
          account,
          marketPlaceContract,
          nftMintingContract,
          marketplaceContractAddress,

          nftMintingContractAddress,
        };
      } else {
        return {
          web3LoadingErrorMessage:
            "Please install an Ethereum-compatible browser or extension like MetaMask to use this dApp!",
        };
      }
    } catch (err) {
      console.log(err);
    }
  }
);

const web3ConnectSlice = createSlice({
  name: "Web3Connect",
  initialState,
  reducers: {
    updateChainId: (state, { payload }) => {
      state.chainId = payload?.networkId;
    },
  },
  extraReducers: {
    [loadWalletConnect.fulfilled.toString()]: (
      state,
      { payload }: PayloadAction<Web3ConnectPayloadType>
    ) => {
      state.web3 = payload?.web3;
      state.account = payload?.account;
      state.web3Loading = false;
      state.marketPlaceContract = payload?.marketPlaceContract;
      state.nftMintingContract = payload?.nftMintingContract;
      state.marketplaceContractAddress = payload?.marketplaceContractAddress;
      state.nftMintingContractAddress = payload?.nftMintingContractAddress;

      state.etherProvider = payload?.etherProvider;
      state.signer = payload?.signer;
      state.swapSdk = payload?.swapSdk;
      state.chainId = payload?.chainId;
      state.paymentTokens = payload?.paymentTokens;

      // state.web3LoadingErrorMessage = payload?.web3LoadingErrorMessage
    },
    [loadBlockchain.fulfilled.toString()]: (
      state,
      { payload }: PayloadAction<Web3ConnectPayloadType>
    ) => {
      state.web3 = payload?.web3;
      state.account = payload?.account;
      state.web3Loading = false;
      state.marketPlaceContract = payload?.marketPlaceContract;
      state.nftMintingContract = payload?.nftMintingContract;
      state.marketplaceContractAddress = payload?.marketplaceContractAddress;
      state.nftMintingContractAddress = payload?.nftMintingContractAddress;
      state.web3LoadingErrorMessage = payload?.web3LoadingErrorMessage;
      state.etherProvider = payload?.etherProvider;
      state.signer = payload?.signer;
      state.swapSdk = payload?.swapSdk;
      state.chainId = payload?.chainId;
      state.paymentTokens = payload?.paymentTokens;
      state.moralisConfig = payload?.moralisConfig;
    },

    [logoutBlockchain.fulfilled.toString()]: (
      state,
      { payload }: PayloadAction<Web3ConnectPayloadType>
    ) => {
      state.web3 = null;
      state.account = null;
      state.web3Loading = false;
      state.marketPlaceContract = null;
      state.nftMintingContract = null;
      state.marketplaceContractAddress = null;
      state.nftMintingContractAddress = null;
      state.web3LoadingErrorMessage = null;
      state.etherProvider = null;
      state.signer = null;
      state.swapSdk = null;
      state.chainId = null;
      state.paymentTokens = null;
      state.moralisConfig = null;
    },
  },
});

export const web3Reducer = web3ConnectSlice.reducer;
export const { updateChainId } = web3ConnectSlice.actions;
